class BundlePicker extends HTMLElement {
  connectedCallback() {
    this.currentVariantId = this.getAttribute('data-current-variant-id');

    this.tierRadios = Array.from(this.querySelectorAll('[data-jp-bundle-tier-radio]'));
    this.submitBtn = this.querySelector('[data-jp-bundle-submit]');
    this.errorEl = this.querySelector('[data-jp-bundle-error]');
    this.modal = this.querySelector('[data-jp-bundle-modal]');
    this.modalCloseBtn = this.querySelector('[data-jp-bundle-modal-close]');
    this.modalOptions = Array.from(this.querySelectorAll('[data-jp-bundle-flavor-option]'));

    this.openSlotIndex = null;
    this.slotVariantIds = [];

    const checkedRadio = this.tierRadios.find((radio) => radio.checked) || this.tierRadios[0];
    if (checkedRadio) this.selectTier(checkedRadio);

    this.tierRadios.forEach((radio) => {
      radio.addEventListener('change', () => this.selectTier(radio));
    });

    this.querySelectorAll('[data-jp-bundle-slot-row]').forEach((row) => {
      row.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-jp-bundle-slot]');
        if (!chip) return;

        const ownerRadio = row.closest('.jp-bundle-tier')?.querySelector('[data-jp-bundle-tier-radio]');
        if (ownerRadio && !ownerRadio.checked) {
          ownerRadio.checked = true;
          this.selectTier(ownerRadio);
        }

        this.openModal(parseInt(chip.dataset.jpBundleSlot, 10));
      });
    });

    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener('click', () => this.closeModal());
    }
    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    this.modalOptions.forEach((option) => {
      option.addEventListener('click', () => this.chooseFlavor(option));
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', () => this.addBundleToCart(this.submitBtn));
    }
  }

  selectTier(radio) {
    this.tierRadios.forEach((r) => {
      const row = r.closest('.jp-bundle-tier');
      if (row) row.classList.toggle('is-selected', r === radio);
    });

    const qty = parseInt(radio.dataset.qty, 10) || 1;
    this.slotVariantIds = new Array(qty).fill(this.currentVariantId);

    const slotRow = radio.closest('.jp-bundle-tier')?.querySelector('[data-jp-bundle-slot-row]');
    if (slotRow) {
      slotRow.querySelectorAll('[data-jp-bundle-slot]').forEach((chip) => this.renderEmptyChip(chip));
    }
  }

  renderEmptyChip(chip) {
    chip.classList.remove('is-filled');
    chip.innerHTML = `
      <span class="jp-bundle-slot-chip-icon">+</span>
      <span class="jp-bundle-slot-chip-label">Choose</span>
    `;
  }

  renderFilledChip(chip, { title, image }) {
    chip.classList.add('is-filled');
    const imgHtml = image
      ? `<img class="jp-bundle-slot-chip-img" src="${image}" alt="" loading="lazy" width="32" height="32">`
      : '';
    chip.innerHTML = `
      ${imgHtml}
      <span class="jp-bundle-slot-chip-label">${title}</span>
    `;
  }

  formatMoney(cents) {
    return `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;
  }

  updateModalPrices() {
    const checkedRadio = this.tierRadios.find((r) => r.checked);
    const unitPrice = checkedRadio ? parseInt(checkedRadio.dataset.unitPrice, 10) : NaN;
    if (isNaN(unitPrice)) return;

    const formatted = this.formatMoney(unitPrice);
    this.modalOptions.forEach((option) => {
      const priceEl = option.querySelector('[data-flavor-current-price]');
      if (priceEl) priceEl.textContent = formatted;
    });
  }

  openModal(slotIndex) {
    if (!this.modal) return;
    this.openSlotIndex = slotIndex;
    this.updateModalPrices();
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.openSlotIndex = null;
  }

  chooseFlavor(option) {
    if (this.openSlotIndex === null || option.disabled) return;

    const variantId = option.dataset.flavorVariantId;
    const title = option.dataset.flavorTitle;
    const image = option.dataset.flavorImage;
    this.slotVariantIds[this.openSlotIndex] = variantId;

    const checkedRadio = this.tierRadios.find((r) => r.checked);
    const slotRow = checkedRadio?.closest('.jp-bundle-tier')?.querySelector('[data-jp-bundle-slot-row]');
    const chip = slotRow?.querySelector(`[data-jp-bundle-slot="${this.openSlotIndex}"]`);
    if (chip) this.renderFilledChip(chip, { title, image });

    this.closeModal();
  }

  getItems() {
    return this.slotVariantIds.map((id) => ({ id: parseInt(id, 10), quantity: 1 }));
  }

  /* triggerBtn: whichever button actually invoked this — the dedicated
     bundle submit button when present, otherwise the page's own "Add to
     cart" button — since either can drive the bundle's add-to-cart. */
  addBundleToCart(triggerBtn) {
    if (!this.slotVariantIds.length) return;
    const items = this.getItems();

    /* .jp2-add-to-cart has its own inner <span class="jp2-btn-text"> +
       loading-spinner markup (shared with Dawn's product-form) — update
       that span instead of clobbering the button's whole contents. The
       bundle-picker's own submit button has no such structure. */
    const textEl = triggerBtn ? triggerBtn.querySelector('.jp2-btn-text') || triggerBtn : null;
    const originalText = textEl ? textEl.textContent : null;
    if (triggerBtn) triggerBtn.disabled = true;
    if (textEl) textEl.textContent = 'Adding…';
    if (this.errorEl) this.errorEl.hidden = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((err) => Promise.reject(err))))
      .then(() => {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-picker' });
        }
      })
      .catch((err) => {
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = (err && err.description) || 'Something went wrong. Please try again.';
        }
      })
      .finally(() => {
        if (triggerBtn) triggerBtn.disabled = false;
        if (textEl) textEl.textContent = originalText;
      });
  }
}

customElements.define('bundle-picker', BundlePicker);
